import { AutoStories, CloudQueue, Folder, OpenInNew, Public } from '@mui/icons-material';
import {
	Box,
	Link,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import type React from 'react';
import {
	menuItemBorderRadius,
	menuLabelSx,
	menuLabelTypographyVariant,
	menuSectionSx,
} from './Constants';

/**
 * Resource link configuration.
 */
interface ResourceLink {
	label: string;
	url: string;
	icon: React.ReactElement;
}

/**
 * External resource links for the campaign.
 */
const resourceLinks: ResourceLink[] = [
	{
		label: 'SW5e Rules',
		url: 'https://sw5e.com/',
		icon: <AutoStories />,
	},
	{
		label: 'Campaign Wiki',
		url: 'http://swlotf.wikidot.com/',
		icon: <Public />,
	},
	{
		label: 'Shared Drive',
		url: 'https://drive.google.com/drive/folders/0B0DnV-NrBZTZbHNZb0QzNXRNdE0?usp=sharing',
		icon: <Folder />,
	},
	{
		label: 'Roll20',
		url: 'https://app.roll20.net/campaigns/details/3130121/starships-and-krayt-dragons-legends-of-the-fallen',
		icon: <CloudQueue />,
	},
];

/**
 * Datapad menu enumeration of external resources used by the campaign.
 *
 * @remarks
 * Links open in new tabs with proper security attributes (noopener, noreferrer).
 * Each link has an icon for visual recognition and an external link indicator.
 */
export function ResourceLinksList(): React.ReactElement {
	return (
		<Box sx={menuSectionSx}>
			<Typography variant={menuLabelTypographyVariant} sx={menuLabelSx}>
				Resources
			</Typography>
			<List disablePadding>
				{resourceLinks.map((resource) => (
					<ListItemButton
						key={resource.url}
						component={Link}
						href={resource.url}
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							borderRadius: menuItemBorderRadius,
							mb: 0.5,
							/*
							 * Remove default link underline for cleaner appearance.
							 * The hover effect provides sufficient visual feedback.
							 */
							textDecoration: 'none',
							'&:hover': {
								textDecoration: 'none',
							},
						}}
					>
						<ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
							{resource.icon}
						</ListItemIcon>
						<ListItemText
							primary={resource.label}
							primaryTypographyProps={{ color: 'text.primary' }}
						/>
						{/*
						 * External link indicator shows these links open in new tabs.
						 */}
						<OpenInNew fontSize="small" sx={{ color: 'text.disabled', ml: 1 }} />
					</ListItemButton>
				))}
			</List>
		</Box>
	);
}
